import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import { useState, useMemo } from 'react';

// GraphQL query for searching Formstack forms
const SEARCH_FORMSTACK_FORMS = gql`
  query SearchFormstackForms($queryString: String!) {
    searchFormstackForms(queryString: $queryString) {
      form_id
      name
    }
  }
`;

/**
 * Custom hook for searching Formstack forms
 * @param {number} minSearchLength - Minimum characters required before searching (default: 4)
 * @param {Object} options - Additional options
 * @param {boolean} options.prefetch - Whether to fetch all forms on mount (for resolving existing form_ids)
 * @returns {Object} - Search state and handlers
 */
export const useFormstackSearch = (
  minSearchLength = 4,
  { prefetch = false } = {}
) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Initial fetch to resolve existing form_ids
  const { data: prefetchData, loading: prefetchLoading } = useQuery(
    SEARCH_FORMSTACK_FORMS,
    {
      variables: { queryString: '' },
      skip: !prefetch,
    }
  );

  const {
    data: searchData,
    loading: searchLoading,
    refetch,
  } = useQuery(SEARCH_FORMSTACK_FORMS, {
    variables: { queryString: searchQuery },
    skip: searchQuery.length < minSearchLength,
  });

  const options = useMemo(() => {
    const forms =
      searchData?.searchFormstackForms ||
      prefetchData?.searchFormstackForms ||
      [];
    return forms.map((form) => ({
      label: form.name,
      value: form.form_id,
    }));
  }, [searchData, prefetchData]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.length >= minSearchLength) {
      refetch({ queryString: query });
    }
  };

  return {
    options,
    loading: searchLoading || prefetchLoading,
    searchQuery,
    handleSearch,
    minSearchLength,
  };
};

export default useFormstackSearch;
